import HeaderWrapper from '@/components/common/Header/HeaderWrapper';
import ImageCropper from '@/components/common/ImageCropper/cropper';
import {
  StepOneDefaultValues,
  stepOneSchema,
  StepTwoDefaultValues,
  stepTwoSchema,
} from '@/components/common/UpdateProfile/Schemas';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/sonner';
import { UPDATE_USER } from '@/Graphql/mutation';
import { GET_USER_BY_ID } from '@/Graphql/queries';
import useAwsUpload from '@/hooks/useAwsUpload';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import { handleError } from '@/lib/utils';
import SignupStepOne from '@/pages/authentication/components/SignupStepOne';
import SignupStepTwo from '@/pages/authentication/components/SignupStepTwo';
import { getRoutes } from '@/routes/routePaths';
import { FileType, HeightType, WeightType } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import imageCompression from 'browser-image-compression';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

const UserSchema = stepOneSchema.merge(stepTwoSchema);
export type UserSchemaType = z.infer<typeof UserSchema>;

const EditProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const step = location?.state?.form || 1;
  const { upload } = useAwsUpload();
  const { getCurrentUser } = useGetCurrentUser();
  const { userDetails } = useReactiveVar(authState);

  const { data } = useQuery(GET_USER_BY_ID, {
    variables: { id: userDetails?._id ?? '' },
  });
  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: async () => {
      await getCurrentUser();
      showToast({ variant: 'success', message: 'Profile updated successfully' });
      navigate(getRoutes.Profile);
    },
    onError: handleError,
    refetchQueries: ['GetUserById', 'GetCurrentUser'],
  });

  const methods = useForm<UserSchemaType>({
    resolver: zodResolver(step === 1 ? stepOneSchema : stepTwoSchema),
    defaultValues: step === 1 ? StepOneDefaultValues : StepTwoDefaultValues,
  });

  const [loading, setLoading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [croppedImage, setCroppedImage] = useState<File | undefined>(undefined);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const imageRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data && data.getUserById) {
      const user = data.getUserById;
      methods.setValue('firstName', user.firstName || '');
      methods.setValue('lastName', user.lastName || '');
      methods.setValue('email', user.email || '');
      methods.setValue('ageRange', user.ageRange || '');
      methods.setValue('gender', user.gender || '');
      methods.setValue('weight', user.weight ? user.weight / 1000 : 0); // Convert grams to kg
      methods.setValue('height', user.height || 0);
      methods.setValue('heightType', user.heightType || HeightType.Centimeter);
      methods.setValue('weightType', user.weightType || WeightType.Kilogram);
      methods.setValue('location', user.locationName || '');
      setProfileImageUrl(user.profileImage || '');
      methods.setValue('languages', user.languages as Array<string>);
      // methods.setValue('fitnessGoals', user.fitnessGoals || '');
      methods.setValue('equipments', user.equipments as Array<string>);
      // methods.setValue(
      //   'typeOfWorkouts',
      //   user?.preferredWorkouts?.map((item: any) => item?._id || '') || [],
      // );
      methods.setValue('isPhysicalActivities', user.currentEngagements?.status || false);
      methods.setValue('physicalActivities', user.currentEngagements?.description || '');
      // methods.setValue('workoutInterval', user.workoutPreference || '');
      methods.setValue('contactVia', user.heardAboutUs || '');
      methods.setValue('terms', true);
    }
  }, [data]);

  const onSubmit = methods.handleSubmit(async (data) => {
    try {
      setLoading(true);
      let uploadedImage;
      if (croppedImage) {
        const selectedFile = {
          name: croppedImage?.name,
          file: croppedImage,
        };
        uploadedImage = await upload(selectedFile, FileType.Photo);
      }
      const stepData =
        step === 1
          ? {
            _id: userDetails?._id,
            firstName: data.firstName,
            lastName: data.lastName,
            ...(uploadedImage?.key && { profileImage: uploadedImage.key }),
            ...(profileImageUrl.length === 0 && !uploadedImage?.key && { profileImage: null }),
            weight: data.weight && data.weight > 0 ? data.weight * 1000 : null,
            height: data.height && data.height > 0 ? data.height : null,
            heightType: data.heightType || HeightType.Centimeter,
            weightType: data.weightType || WeightType.Kilogram,
            locationName: data.location,
            email: data.email,
            gender: data.gender,
            country: data.country,
            ageRange: data.ageRange,
            languages: data.languages,
          }
          : {
            _id: userDetails?._id,
            // fitnessGoals: data.fitnessGoals,
            equipments: data.equipments,
            // preferredWorkoutsIds: data.typeOfWorkouts,
            currentEngagements: {
              description: data.physicalActivities,
              status: data.isPhysicalActivities,
            },
            // workoutPreference: data.workoutInterval,
            heardAboutUs: data.contactVia,
          };
      await updateUser({ variables: { data: stepData } });
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  });

  const handleImageClick = () => {
    if (selectedImage || profileImageUrl) {
      setProfileImageUrl('');
      setSelectedImage(undefined);
      setCroppedImage(undefined);
    } else {
      imageRef.current?.click();
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
          fileType: 'image/jpeg',
        };
        const compressedFile = await imageCompression(file, options);
        setSelectedImage(compressedFile);
        setCropperOpen(true);
        if (imageRef.current) {
          imageRef.current.value = '';
        }
      } catch (error) {
        console.error('Error compressing image:', error);
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className='w-full  flex flex-col overflow-auto'>
          <HeaderWrapper className='fixed w-full top-0 z-50 flex-col md:flex-row gap-2'>
            <div className='flex w-full items-center gap-2'>
              <Button
                onClick={() => navigate(-1)}
                id='session-details-back-btn'
                variant='ghost'
                size='icon'
                className='text-white'
              >
                <ArrowLeft className='h-5 w-5' />
              </Button>
              <h1 className='text-lg font-semibold'>Edit Profile</h1>
            </div>
            <div className='flex items-center gap-4'>
              <Button
                id='session-detail-more-options'
                variant='secondary'
                size='sm'
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                id='session-detail-more-options'
                variant='default'
                size='sm'
                type='submit'
                loading={loading}
              >
                Save
              </Button>
            </div>
          </HeaderWrapper>
          <div className='w-full h-full flex  justify-center mt-20 mb-16'>
            <div className='w-[95%] max-w-[640px]'>
              {step === 1 ? (
                <SignupStepOne
                  croppedImage={croppedImage}
                  imageRef={imageRef}
                  handleImageChange={handleImageChange}
                  handleImageClick={handleImageClick}
                  isEdit={true}
                  profileImageUrl={profileImageUrl}
                />
              ) : (
                <SignupStepTwo loading={loading} />
              )}
            </div>
          </div>
          <ImageCropper
            setCroppedImageL={setCroppedImage}
            selectedImage={selectedImage}
            aspectRatio={1}
            open={cropperOpen}
            onClose={() => {
              setCropperOpen(false);
              if (imageRef.current) imageRef.current.value = '';
            }}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default EditProfile;
